import React, {
  ChangeEvent,
  Fragment,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import styles from './index.module.scss';
import { StatusBox } from '@components/StatusBox';
import { Select } from '@components/Select';
import { langs } from '@constants/i18n';
import { useTranslation } from 'react-i18next';
import { LS_KEY } from '@constants/storage';
import dayjs from 'dayjs';
import { useOrderStatusService } from '@hooks/api/useOrderStatusService';
import { useLocation } from 'react-router-dom';
import {
  OrderStatusGetV1ResponseOrderStatus,
  OrderstatusLanguage,
} from '@openapi/orderstatus/order_status';
import { Helmet } from 'react-helmet-async';

const POLLING_INTERVAL_MS = 60000;
const POLLING_STATUSES: OrderStatusGetV1ResponseOrderStatus[] = [
  OrderStatusGetV1ResponseOrderStatus.Prepared,
  OrderStatusGetV1ResponseOrderStatus.Ready,
];

const useQueryParams = () => new URLSearchParams(useLocation().search);

const getTranslateName = (
  languages: OrderstatusLanguage[] | undefined,
  currentLang: string,
  fallbackLang: string = langs.ja,
): string | undefined => {
  if (!languages) return undefined;
  const returnName = languages.find((elem) => elem.code === currentLang)?.text;
  const fallbackName = languages.find(
    (elem) => elem.code === fallbackLang,
  )?.text;
  return returnName || fallbackName;
};

export const Home = () => {
  const { t, i18n } = useTranslation();
  const { useOrderStatusServiceOrderStatusGetV1 } = useOrderStatusService();
  const query = useQueryParams();
  const restaurantId = query.get('restaurant_id') ?? '';
  const orderId = query.get('order_id') ?? '';

  const [manualPollingLoading, setManualPollingLoading] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: orderStatusData,
    isLoading: orderStatusIsLoading,
    refetch: orderStatusRefetch,
  } = useOrderStatusServiceOrderStatusGetV1({
    restaurantId,
    orderId,
    useQueryOptions: {
      enabled: !!restaurantId && !!orderId,
      refetchInterval: false,
    },
  });

  const poll = useCallback(async () => {
    setManualPollingLoading(true);
    const start = Date.now();
    await orderStatusRefetch();
    const elapsed = Date.now() - start;
    const remain = 1000 - elapsed;
    setTimeout(() => setManualPollingLoading(false), remain > 0 ? remain : 0);
  }, [orderStatusRefetch]);

  useEffect(() => {
    const status = orderStatusData?.data?.status;
    const shouldPoll = status && POLLING_STATUSES.includes(status);

    if (shouldPoll && pollingIntervalRef.current === null) {
      pollingIntervalRef.current = setInterval(() => {
        poll();
      }, POLLING_INTERVAL_MS);
    }

    if (!shouldPoll && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setManualPollingLoading(false);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [orderStatusData?.data?.status, poll]);

  const changeLanguage = useCallback(
    (language: string) => {
      switch (language) {
        case langs.en:
          dayjs.locale('en');
          break;
        case langs.cn:
          dayjs.locale('zh-cn');
          break;
        case langs.tw:
          dayjs.locale('zh-tw');
          break;
        case langs.ko:
          dayjs.locale('ko');
          break;
        default:
          dayjs.locale('ja');
      }
      i18n.changeLanguage(language);
      localStorage.setItem(LS_KEY.lang, language);
    },
    [i18n],
  );

  useEffect(() => {
    if (orderStatusData?.data?.orderedLanguage) {
      changeLanguage(orderStatusData.data.orderedLanguage);
    }
  }, [orderStatusData?.data?.orderedLanguage, changeLanguage]);

  const onChangedLanguage = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      changeLanguage(value);
    },
    [changeLanguage],
  );

  const handleUpdateStatus = useCallback(async () => {
    setManualPollingLoading(true);

    const start = Date.now();
    await orderStatusRefetch();
    const elapsed = Date.now() - start;
    const remain = 1000 - elapsed;

    if (remain > 0) {
      await new Promise((resolve) => setTimeout(resolve, remain));
    }

    setManualPollingLoading(false);
  }, [orderStatusRefetch]);

  const orderStatus = orderStatusData?.data;
  const currentStatus =
    orderStatus?.status ?? OrderStatusGetV1ResponseOrderStatus.NotFound;
  const showLoading = orderStatusIsLoading || manualPollingLoading;

  return (
    <>
      <Helmet>
        <title>注文確認サービス</title>
        <meta name="description" content="注文確認サービス" />
        <link rel="icon" type="image/png" href="favicon.ico" sizes="96x96" />
        <link
          rel="apple-touch-icon"
          type="image/png"
          href="favicon.ico"
          sizes="180x180"
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.container__mainBox}>
          <StatusBox
            statusType={orderStatus?.status}
            updateStatus={handleUpdateStatus}
            isLoading={showLoading}
          />
          {currentStatus !== OrderStatusGetV1ResponseOrderStatus.NotFound && (
            <Fragment>
              <div className={styles.container__mainBox__receiveNoBox}>
                <div className={styles.container__mainBox__receiveNoBox__title}>
                  {t('receiveId')}
                </div>
                <div className={styles.container__mainBox__receiveNoBox__value}>
                  {orderStatus?.oderNo}
                </div>
              </div>
              <div className={styles.container__mainBox__subBox}>
                <div className={styles.container__mainBox__restaurantBox}>
                  <div
                    className={styles.container__mainBox__restaurantBox__title}
                  >
                    {t('shop')}
                  </div>
                  <div
                    className={styles.container__mainBox__restaurantBox__value}
                  >
                    {getTranslateName(
                      orderStatus?.restaurantName?.languages,
                      i18n.language,
                    )}
                  </div>
                </div>
                <div className={styles.container__mainBox__orderTitle}>
                  {t('orderDetails')}
                </div>
                <table className={styles.container__mainBox__orderTable}>
                  <thead>
                    <tr>
                      <th>{t('menu')}</th>
                      <th>{t('quantity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      className={
                        styles.container__mainBox__orderTable__spacer16
                      }
                    >
                      <td colSpan={2}></td>
                    </tr>
                    {orderStatus?.orderItems?.map(
                      (eachItems, eachItemsIndex) => (
                        <Fragment key={eachItemsIndex}>
                          {eachItemsIndex !== 0 && (
                            <tr
                              className={
                                styles.container__mainBox__orderTable__spacer12
                              }
                            >
                              <td colSpan={2}></td>
                            </tr>
                          )}
                          <tr>
                            <td>
                              ＊
                              {getTranslateName(
                                eachItems?.name?.languages,
                                i18n.language,
                              )}
                            </td>
                            <td>{eachItems.quantity}</td>
                          </tr>
                          {eachItems.customizeItems?.map(
                            (eachCustomizeItems, eachCustomizeItemsIndex) => (
                              <Fragment key={eachCustomizeItemsIndex}>
                                <tr
                                  className={
                                    styles.container__mainBox__orderTable__spacer6
                                  }
                                >
                                  <td colSpan={2}></td>
                                </tr>
                                <tr
                                  className={
                                    styles.container__mainBox__orderTable__subOrder
                                  }
                                >
                                  <td>
                                    -
                                    {getTranslateName(
                                      eachCustomizeItems?.name?.languages,
                                      i18n.language,
                                    )}
                                  </td>
                                  <td>{eachCustomizeItems.quantity}</td>
                                </tr>
                              </Fragment>
                            ),
                          )}
                        </Fragment>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </Fragment>
          )}
        </div>
        <div className={styles.container__languageBox}>
          <div className={styles.container__languageBox__title}>Language</div>
          <Select
            className={styles.container__languageBox__selectLang}
            onChange={onChangedLanguage}
            value={i18n.language}
            isRequired
          >
            <option value={langs.ja}>日本語</option>
            <option value={langs.en}>English</option>
            <option value={langs.cn}>簡体中文</option>
            <option value={langs.tw}>繁体中文</option>
            <option value={langs.ko}>한국어</option>
          </Select>
        </div>
      </div>
    </>
  );
};
